import {ReplaySubject} from "rxjs/ReplaySubject";
import {Breakpoint, Breakpoints} from "./Breakpoint";
import {Observable} from "rxjs/Observable";
import {SystemEventService} from "../system-state/SystemEventService";
import {SystemEvent} from "../system-state/SystemEvent";
import {Scheduler} from "rxjs";

export interface ObservedBreakpoint {
    active: boolean;
    breakpoint: Breakpoint;
}

export interface BreakpointChange {
    current: Breakpoint;
    previous: Breakpoint;
}

export class BreakpointObserver extends ReplaySubject<BreakpointChange> {

    public static id = "breakpointObserver";

    private currentBp: Breakpoint;

    private readonly screenChange = new ReplaySubject<boolean>(1);

    /** @ngInject */
    public constructor(private systemEventService: SystemEventService) {
        super(1);
        this.init();
    }

    private init() {
        this.updateCurrentBreakpoint();
        this.systemEventService
            .filter(evt => evt === SystemEvent.VIEW_RESIZE)
            .subscribe(this.updateCurrentBreakpoint.bind(this));
    }

    private updateCurrentBreakpoint() {
        this.screenChange.next(true);
        for (const bp of Breakpoints) {
            if (this.isActive(bp) && this.currentBp !== bp) {
                this.breakpointChanged(bp);
                break;
            }
        }
    }

    private breakpointChanged(newBp: Breakpoint) {
        const previous = this.currentBp;
        this.currentBp = newBp;
        const change = {
            previous,
            current: newBp
        };
        this.next(change);
    }

    public current(): Breakpoint {
        return this.currentBp;
    }

    public isActive(breakpoint: Breakpoint): boolean {
        return window.matchMedia(breakpoint).matches;
    }

    /**
     * Will emit a new item every time the supplied Breakpoint is either activated or deactivated
     *
     * @deprecated Use #observse instead because it isn't terrible like this one is
     */
    public watch(breakpoint: Breakpoint): Observable<ObservedBreakpoint> {
        return this.filter(change => change.current === breakpoint || change.previous === breakpoint)
                   .map(change => this.toObservedBreakpoint(breakpoint, change))
                   .startWith(this.breakpointValue(breakpoint));
    }

    public observe(breakpoint: Breakpoint) {
        const currentValue = this.breakpointValue(breakpoint);
        return this.screenChange
                   .observeOn(Scheduler.async)
                   .map(() => this.breakpointValue(breakpoint))
                   .startWith(currentValue)
                   .distinctUntilKeyChanged("active");
    }

    private breakpointValue(breakpoint: Breakpoint): ObservedBreakpoint {
        const active = this.isActive(breakpoint);
        return {
            active,
            breakpoint
        };
    }

    private toObservedBreakpoint(breakpoint: Breakpoint, change: BreakpointChange): ObservedBreakpoint {
        const active = change.current === breakpoint;
        return {
            active,
            breakpoint
        };
    }
}
